import { useCallback, useEffect, useState } from "react";

type TestFn = (width: number) => boolean;

type Config = Record<string, TestFn>;

const useScreenWidth = () => {
  const [windowWidth, setWindowWidth] = useState<number>(null!);

  const isWindow = typeof window !== "undefined";

  const getWidth = useCallback(() => (isWindow ? window.innerWidth : windowWidth), [isWindow, windowWidth]);

  const resize = useCallback(() => setWindowWidth(getWidth()), [getWidth]);

  useEffect(() => {
    if (isWindow) {
      resize();

      window.addEventListener("resize", resize);

      return () => window.removeEventListener("resize", resize);
    }
  }, [getWidth, isWindow, resize]);

  return windowWidth;
};

const useBreakpoints = <T extends Config>(config: T): Record<keyof T, boolean> => {
  const windowWidth = useScreenWidth();

  return Object.fromEntries(
    Object.entries(config).map(([key, test]) => [key, test(windowWidth)]),
  ) as Record<keyof T, boolean>;
};

export default useBreakpoints;